/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * My profile › Notifications: which emails you get. Push is set in the app.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
} from "@lemonadetraining/web-ui";
import { toast } from "sonner";
import { doc, setDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { firebaseDB, COLLECTIONS } from "@lemonadetraining/web-shared";
import { NotificationPreferences, NotificationCategory } from "@lemonadetraining/contracts";
import SavedIndicator from "./SavedIndicator";

interface RootState {
  user: {
    user: {
      uid: string;
      email: string;
    } | null;
  };
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  categories: {
    bookings: { email: true, push: true },
    payments: { email: true, push: true },
    classUpdates: { email: true, push: true },
    account: { email: true, push: false },
  },
  pushEnabled: true,
  emailEnabled: true,
  unsubscribedAll: false,
  updatedAt: new Date(),
  updatedFrom: "web",
};

// Social is push-only (no email channel), so this email list has no row for it.
type OptionalCategory = Exclude<NotificationCategory, "account" | "social">;

const ROWS: { category: OptionalCategory; label: string; description: string; note?: string }[] = [
  {
    category: "bookings",
    label: "Bookings",
    description: "Booking confirmations, cancellations, waiting list updates and reminders",
  },
  {
    category: "classUpdates",
    label: "Class updates",
    description: "Class cancellations and schedule changes",
  },
  {
    category: "payments",
    label: "Payments",
    description: "Receipts, membership status and visit cards running out",
    note: "Failed payments and renewal notices are always sent.",
  },
];

/* What the server always sends, whatever is set here (NotificationService `critical`). */
const ALWAYS_SENT =
  "Failed payments, renewal notices, cancellation confirmations and password resets";

/*
 * "Unsubscribe from all" here is `emailEnabled: false`: the server then drops
 * optional email only. `unsubscribedAll` (set by an email's unsubscribe link)
 * also stops app notifications, critical ones included, so it is its own state.
 */
type Change =
  | { category: OptionalCategory; email: boolean }
  | { emailEnabled: boolean }
  | { unsubscribedAll: boolean };

const applyChange = (prefs: NotificationPreferences, change: Change): NotificationPreferences =>
  "category" in change
    ? {
        ...prefs,
        categories: {
          ...prefs.categories,
          [change.category]: { ...prefs.categories[change.category], email: change.email },
        },
      }
    : { ...prefs, ...change };

const NotificationsTab: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const userUID = user?.uid;
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [docExists, setDocExists] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [saved, setSaved] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const docExistsRef = useRef(docExists);
  docExistsRef.current = docExists;
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  useEffect(() => {
    if (!userUID) return undefined;
    setFailed(false);
    const prefsRef = doc(
      firebaseDB,
      COLLECTIONS.Users,
      userUID,
      COLLECTIONS.NotificationPreferences,
      "default"
    );
    return onSnapshot(
      prefsRef,
      (snap) => {
        setPreferences(
          snap.exists() ? (snap.data() as NotificationPreferences) : DEFAULT_PREFERENCES
        );
        setDocExists(snap.exists());
      },
      (err) => {
        console.error("Error loading notification preferences:", err);
        setFailed(true);
      }
    );
  }, [userUID, retry]);

  /** Resolves true only once the write landed; the caller shows "Saved" on true alone. */
  const save = useCallback(
    async (change: Change): Promise<boolean> => {
      const current = preferencesRef.current;
      if (!userUID || !current) return false;
      const next = applyChange(current, change);
      setPreferences(next);
      setSaveFailed(false);
      const prefsRef = doc(
        firebaseDB,
        COLLECTIONS.Users,
        userUID,
        COLLECTIONS.NotificationPreferences,
        "default"
      );
      try {
        if (docExistsRef.current) {
          await updateDoc(prefsRef, {
            ...("category" in change
              ? { [`categories.${change.category}.email`]: change.email }
              : change),
            updatedAt: serverTimestamp(),
            updatedFrom: "web",
          });
        } else {
          await setDoc(prefsRef, {
            ...next,
            updatedAt: serverTimestamp(),
            updatedFrom: "web",
          });
        }
        return true;
      } catch (err) {
        console.error("Error saving notification preference:", err);
        setPreferences(current);
        setSaveFailed(true);
        return false;
      }
    },
    [userUID]
  );

  const onToggle = async (category: OptionalCategory, email: boolean) => {
    setSaved(null);
    if (await save({ category, email })) setSaved(category);
  };

  const onUnsubscribeAll = async () => {
    setSaved(null);
    if (await save({ emailEnabled: false })) {
      toast.success("Unsubscribed from optional emails", {
        duration: 10000,
        action: { label: "Undo", onClick: () => void save({ emailEnabled: true }) },
      });
    }
  };

  const onTurnEverythingBackOn = async () => {
    setSaved(null);
    if (await save({ unsubscribedAll: false })) {
      toast.success("Notifications are back on");
    }
  };

  const onUndoUnsubscribe = async () => {
    if (await save({ emailEnabled: true })) {
      toast.success("Your email choices are back as they were");
    }
  };

  if (failed) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-6 text-sm" role="alert">
          <span className="text-destructive">We couldn&apos;t load your email choices.</span>
          <Button variant="outline" className="min-h-[44px]" onClick={() => setRetry((n) => n + 1)}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <div aria-busy="true" data-testid="notifications-loading">
        <span className="sr-only">Loading</span>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const everythingOff = preferences.unsubscribedAll === true;
  const unsubscribed = everythingOff || preferences.emailEnabled === false;

  return (
    <Card data-testid="notifications-section">
      <CardHeader>
        <CardTitle>Email</CardTitle>
        <CardDescription>
          Choose which emails you get{user?.email ? ` at ${user.email}` : ""}. Push notifications
          are set in the app.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {everythingOff && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3 text-sm"
            data-testid="notifications-unsubscribed-everything"
          >
            <span className="min-w-0 flex-1">
              You unsubscribed from everything through a link in an email. That also stops
              notifications in the app, including failed payments and class changes.
            </span>
            <Button
              variant="outline"
              className="min-h-[44px]"
              onClick={onTurnEverythingBackOn}
              data-testid="notifications-turn-back-on"
            >
              Turn them back on
            </Button>
          </div>
        )}
        {!everythingOff && unsubscribed && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3 text-sm"
            data-testid="notifications-unsubscribed"
          >
            <span className="min-w-0 flex-1">
              You&apos;re unsubscribed from every email you can turn off. {ALWAYS_SENT} still come,
              and app notifications are not affected.
            </span>
            <Button variant="outline" className="min-h-[44px]" onClick={onUndoUnsubscribe}>
              Undo
            </Button>
          </div>
        )}
        {saveFailed && (
          <p
            role="alert"
            className="text-sm text-destructive"
            data-testid="notifications-save-failed"
          >
            Not saved. Please try again.
          </p>
        )}

        <div className="flex flex-col divide-y">
          {ROWS.map((row) => {
            const id = `notif-${row.category}`;
            return (
              <div key={row.category} className="flex items-center gap-3 py-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <Label htmlFor={id} className="text-sm font-medium">
                      {row.label}
                    </Label>
                    <SavedIndicator
                      visible={saved === row.category}
                      onHidden={() => setSaved(null)}
                      label="Saved"
                    />
                  </span>
                  <span id={`${id}-desc`} className="text-xs text-muted-foreground">
                    {row.description}
                    {row.note ? `. ${row.note}` : ""}
                  </span>
                </div>
                <Switch
                  id={id}
                  checked={!unsubscribed && preferences.categories[row.category]?.email !== false}
                  disabled={unsubscribed}
                  onCheckedChange={(next: boolean) => onToggle(row.category, next)}
                  aria-describedby={`${id}-desc`}
                  data-testid={`notifications-toggle-${row.category}`}
                />
              </div>
            );
          })}
          <div className="flex items-center gap-3 py-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-sm font-medium">Account</span>
              <span className="text-xs text-muted-foreground">
                Password resets and security alerts
              </span>
            </div>
            <Badge variant="outline" className="shrink-0">
              Always sent
            </Badge>
          </div>
        </div>

        {!unsubscribed && (
          <div>
            <Button
              variant="ghost"
              className="min-h-[44px]"
              onClick={onUnsubscribeAll}
              data-testid="notifications-unsubscribe-all"
            >
              Unsubscribe from all optional emails
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationsTab;
